/**
 * Centralized PostHog event names used across the application.
 *
 * Uses a string enum with **dot-separated feature prefixes** for easy
 * identification, filtering, and grouping in PostHog dashboards.
 *
 * ### Prefix Convention
 * | Prefix          | Feature Area                              |
 * |-----------------|-------------------------------------------|
 * | `auth.*`        | Authentication (sign-up, login, logout)    |
 * | `billing.*`     | Billing & payments                         |
 * | `audit.*`       | Site audit workflows                       |
 * | `plugin.*`      | Browser extension / plugin                 |
 * | `keyword.*`     | Keyword research tools                     |
 * | `local_seo.*`   | Local SEO features                         |
 * | `orbit_ai.*`    | Orbit AI agent features                    |
 * | `ai_visibility.*` | AI Visibility module                     |
 * | `onboarding.*`  | Onboarding flow                            |
 * | `report.*`      | PDF / SEO report features                  |
 * | `lead_gen.*`    | Lead generation                            |
 * | `review.*`      | Google reviews & widgets                   |
 * | `citation.*`    | Citation builder                           |
 * | `team.*`        | User/team management                       |
 * | `gsc.*`         | Google Search Console                      |
 * | `settings.*`    | User settings                              |
 * | `backlink.*`    | Backlink analysis                          |
 * | `claude_mcp.*`   | Claude MCP integration                     |
 *
 * @example
 * ```ts
 * import { PostHogEvent } from 'src/common/services/posthogEvents';
 *
 * posthogService.track(PostHogEvent.AUTH_USER_SIGNED_UP, { email });
 * posthogService.track(PostHogEvent.BILLING_PAYMENT_SUCCESSFUL, { amount: 49 });
 * ```
 */
export enum PostHogEvent {
  // ── Auth ──────────────────────────────────────────────────────────────────
  AUTH_USER_SIGNED_UP = 'auth.user_signed_up',
  AUTH_USER_LOGGED_IN = 'auth.user_logged_in',
  AUTH_USER_LOGGED_OUT = 'auth.user_logged_out',
  AUTH_PASSWORD_RESET_REQUESTED = 'auth.password_reset_requested',

  // ── Billing & Payments ────────────────────────────────────────────────────
  BILLING_FREE_PLAN_SELECTED = 'billing.free_plan_selected',
  BILLING_TRIAL_PLAN_SELECTED = 'billing.trial_plan_selected',
  BILLING_BEGIN_CHECKOUT = 'billing.begin_checkout',
  BILLING_PAYMENT_SUCCESSFUL = 'billing.payment_successful',
  BILLING_PLAN_UPGRADED = 'billing.plan_upgraded',
  BILLING_PLAN_DOWNGRADED = 'billing.plan_downgraded',
  BILLING_ADDON_PURCHASED = 'billing.addon_purchased',
  BILLING_PLAN_SELECTED = 'billing.plan_selected',

  // ── Plugin / Extension ────────────────────────────────────────────────────
  PLUGIN_INSTALL_CLICKED = 'plugin.install_clicked',
  PLUGIN_CHROME_EXTENSION_MODAL_SHOWN = 'plugin.chrome_extension_modal_shown',

  // ── Site Audit ────────────────────────────────────────────────────────────
  AUDIT_SITE_AUDIT_COMPLETED = 'audit.site_audit_completed',
  AUDIT_SCAN_STARTED = 'audit.scan_started',
  AUDIT_RE_AUDIT_STARTED = 'audit.re_audit_started',
  AUDIT_SITE_ADDED = 'audit.site_added',
  AUDIT_SITE_DELETED = 'audit.site_deleted',

  // ── Keyword Research ──────────────────────────────────────────────────────
  KEYWORD_SEARCH_PERFORMED = 'keyword.search_performed',
  KEYWORD_TRACKING_ADDED = 'keyword.tracking_added',
  KEYWORD_LIST_CREATED = 'keyword.list_created',
  KEYWORD_COMPETITOR_ANALYZED = 'keyword.competitor_analyzed',
  KEYWORD_CREDITS_PURCHASED = 'keyword.credits_purchased',

  // ── Local SEO ─────────────────────────────────────────────────────────────
  LOCAL_SEO_PROFILE_CONNECTED = 'local_seo.profile_connected',
  LOCAL_SEO_PROFILE_DELETED = 'local_seo.profile_deleted',
  LOCAL_SEO_POST_CREATED = 'local_seo.post_created',
  LOCAL_SEO_POST_DELETED = 'local_seo.post_deleted',
  LOCAL_SEO_REVIEW_REPLIED = 'local_seo.review_replied',

  // ── Orbit AI ──────────────────────────────────────────────────────────────
  // Navigation & Hub
  ORBIT_AI_HUB_TOOL_CLICKED = 'orbit_ai.hub_tool_clicked',

  // Addon & Monetization
  ORBIT_AI_ADDON_MODAL_OPENED = 'orbit_ai.addon_modal_opened',
  ORBIT_AI_ADDON_MODAL_CLOSED = 'orbit_ai.addon_modal_closed',
  ORBIT_AI_ADDON_INTERVAL_CHANGED = 'orbit_ai.addon_interval_changed',
  ORBIT_AI_ADDON_COUPON_APPLIED = 'orbit_ai.addon_coupon_applied',
  ORBIT_AI_ADDON_COUPON_REMOVED = 'orbit_ai.addon_coupon_removed',
  ORBIT_AI_ADDON_PURCHASE_STARTED = 'orbit_ai.addon_purchase_started',
  ORBIT_AI_ADDON_PURCHASED = 'orbit_ai.addon_purchased',
  ORBIT_AI_ADDON_PURCHASE_FAILED = 'orbit_ai.addon_purchase_failed',

  // Agent Lifecycle & Toggle State
  ORBIT_AI_AGENT_TOGGLED = 'orbit_ai.agent_toggled',

  // Onboarding Flow
  ORBIT_AI_ONBOARDING_VIEWED = 'orbit_ai.onboarding_viewed',
  ORBIT_AI_ONBOARDING_FEATURE_TOGGLED = 'orbit_ai.onboarding_feature_toggled',
  ORBIT_AI_ONBOARDING_COMPLETED = 'orbit_ai.onboarding_completed',
  ORBIT_AI_ONBOARDING_SKIPPED = 'orbit_ai.onboarding_skipped',

  // Keyword Assist Agent
  ORBIT_AI_KEYWORD_ASSIST_RUN = 'orbit_ai.keyword_assist_run',
  ORBIT_AI_KEYWORD_ASSIST_DIALOG_OPENED = 'orbit_ai.keyword_assist_dialog_opened',
  ORBIT_AI_KEYWORD_ASSIST_SEARCHED = 'orbit_ai.keyword_assist_searched',
  ORBIT_AI_KEYWORD_ASSIST_FILTER_CHANGED = 'orbit_ai.keyword_assist_filter_changed',
  ORBIT_AI_KEYWORD_ASSIST_TAB_CHANGED = 'orbit_ai.keyword_assist_tab_changed',
  ORBIT_AI_KEYWORD_ASSIST_COPIED = 'orbit_ai.keyword_assist_copied',

  // Content Strategy Agent
  ORBIT_AI_CONTENT_STRATEGY_CREATED = 'orbit_ai.content_strategy_created',
  ORBIT_AI_CONTENT_STRATEGY_DIALOG_OPENED = 'orbit_ai.content_strategy_dialog_opened',
  ORBIT_AI_PAGE_PLAN_GENERATED = 'orbit_ai.page_plan_generated',
  ORBIT_AI_PAGE_PLAN_VIEWED = 'orbit_ai.page_plan_viewed',
  ORBIT_AI_OPPORTUNITY_ADDED = 'orbit_ai.opportunity_added_to_plan',
  ORBIT_AI_OPPORTUNITY_IGNORED = 'orbit_ai.opportunity_ignored',
  ORBIT_AI_CLUSTER_BLUEPRINTS_GENERATED = 'orbit_ai.cluster_blueprints_generated',

  // Blog Post Agent
  ORBIT_AI_BLOG_BLUEPRINTS_GENERATED = 'orbit_ai.blog_blueprints_generated',
  ORBIT_AI_BLOG_TOPICS_DIALOG_OPENED = 'orbit_ai.blog_topics_dialog_opened',
  ORBIT_AI_BLOG_SEARCHED = 'orbit_ai.blog_searched',
  ORBIT_AI_BLOG_GENERATION_STARTED = 'orbit_ai.blog_generation_started',
  ORBIT_AI_BLOG_GENERATED = 'orbit_ai.blog_generated',
  ORBIT_AI_BLOG_VIEWED = 'orbit_ai.blog_viewed',
  ORBIT_AI_BLOG_COPIED = 'orbit_ai.blog_copied',

  // Local SEO AI Agent
  ORBIT_AI_LOCAL_SEO_RUN = 'orbit_ai.local_seo_run',
  ORBIT_AI_LOCAL_SEO_DIALOG_OPENED = 'orbit_ai.local_seo_dialog_opened',
  ORBIT_AI_LOCAL_SEO_INSIGHT_CLICKED = 'orbit_ai.local_seo_insight_clicked',
  ORBIT_AI_LOCAL_SEO_ACTION_APPLIED = 'orbit_ai.local_seo_action_applied',

  // Review Automation Agent
  ORBIT_AI_REVIEW_SETTINGS_OPENED = 'orbit_ai.review_settings_opened',
  ORBIT_AI_REVIEW_SETTINGS_SAVED = 'orbit_ai.review_settings_saved',
  ORBIT_AI_REVIEW_FILTER_CHANGED = 'orbit_ai.review_filter_changed',
  ORBIT_AI_REVIEW_RESPONSE_POSTED = 'orbit_ai.review_response_posted',
  ORBIT_AI_REVIEW_REPLY_COPIED = 'orbit_ai.review_reply_copied',

  // ── AI Visibility ─────────────────────────────────────────────────────────
  AI_VISIBILITY_ANALYSIS_STARTED = 'ai_visibility.analysis_started',
  AI_VISIBILITY_ADDON_PURCHASED = 'ai_visibility.addon_purchased',

  // ── Onboarding ────────────────────────────────────────────────────────────
  ONBOARDING_STARTED = 'onboarding.started',
  ONBOARDING_COMPLETED = 'onboarding.completed',
  ONBOARDING_SKIPPED = 'onboarding.skipped',

  // ── Reports ───────────────────────────────────────────────────────────────
  REPORT_PDF_GENERATED = 'report.pdf_generated',
  REPORT_PDF_SHARED = 'report.pdf_shared',

  // ── Lead Generation ───────────────────────────────────────────────────────
  LEAD_GEN_FORM_CREATED = 'lead_gen.form_created',
  LEAD_GEN_FORM_SUBMITTED = 'lead_gen.form_submitted',

  // ── Google Reviews & Widgets ──────────────────────────────────────────────
  REVIEW_WIDGET_CREATED = 'review.widget_created',

  // ── Citation Builder ──────────────────────────────────────────────────────
  CITATION_CAMPAIGN_CREATED = 'citation.campaign_created',
  CITATION_CAMPAIGN_PAID = 'citation.campaign_paid',

  // ── Team / User Management ────────────────────────────────────────────────
  TEAM_MEMBER_INVITED = 'team.member_invited',
  TEAM_MEMBER_REMOVED = 'team.member_removed',

  // ── Google Search Console ─────────────────────────────────────────────────
  GSC_CONNECTED = 'gsc.connected',
  GSC_DISCONNECTED = 'gsc.disconnected',

  // ── Settings ──────────────────────────────────────────────────────────────
  SETTINGS_PROFILE_UPDATED = 'settings.profile_updated',

  // ── Backlinks ─────────────────────────────────────────────────────────────
  BACKLINK_ANALYSIS_STARTED = 'backlink.analysis_started',

  // ── Claude MCP ────────────────────────────────────────────────────────────
  CLAUDE_MCP_MODAL_OPENED = 'claude_mcp.modal_opened',
  CLAUDE_MCP_MODAL_CLOSED = 'claude_mcp.modal_closed',
  CLAUDE_MCP_CONFIG_COPIED = 'claude_mcp.config_copied',
  CLAUDE_MCP_SERVER_URL_COPIED = 'claude_mcp.server_url_copied'
}
